import Fuse from 'fuse.js'
import { markRaw } from 'vue'

import Model from '../model'
import Result from '../result'

import { searchManifest, type SearchManifestEntry } from '#build/search-manifest.generated'
import Component from '@/components/search/elements/Settings.vue'
import { groups as accountGroups } from '@/components/account/pages'
import { groups as manageGroups } from '@/components/manage/pages'
import { groups as minisiteGroups } from '@/components/minisite/pages'
import {
  settingsNavigationSearchEntries,
  type SettingsNavigationGroup,
} from '@/lib/settingsSearch'

interface SearchSourceEntry extends SearchManifestEntry {
  pageInfo?: string
  searchTags?: string[]
}

interface IndexedEntry extends SearchSourceEntry {
  /** Pre-resolved translation of whichever label this entry exposes. */
  searchableLabel: string
  /** Pre-resolved translation of whichever info / supporting text this entry exposes. */
  searchableInfo: string
  /** Pre-resolved page title — every entry gets the page title so a search for the page name also surfaces matching fields. */
  searchablePageTitle: string
  /** Pre-resolved section name (for field rows) — the breadcrumb. */
  searchableSectionLabel?: string
  /** Navigation aliases and tags for CRUD/custom settings pages. */
  searchableTags: string
  /** Anchor the result navigates to: pagePath + optional `#sectionId`. */
  href: string
}

/**
 * Universal Settings search bucket. Backed by a static manifest generated at
 * build time by `modules/searchManifest.ts`. Each settings page contributes:
 *
 *   - a `page` entry (title only)
 *   - one `section` entry per labelled section
 *   - one `field` entry per `<SettingsRow>` inside a named slot
 *
 * Field selections navigate to `path#sectionId`; `SettingsLandingPage`'s
 * section anchors handle the scroll. The auth middleware enforces grants /
 * features / gates at navigation time — we also filter by `useAccess` here so
 * unreachable pages never appear in results.
 *
 * Fuse keys are pre-translated strings (not raw i18n keys) so a search for
 * "staff" matches the translated label of `sections.employees`.
 */
class Settings extends Model {
  private cacheKey: string | null = null
  private fuse: Fuse<IndexedEntry> | null = null

  constructor () {
    super({ name: 'Settings', component: Component })
    this.items = [] as IndexedEntry[]
  }

  init (context: { $auth: any; $grants: any; $features: any; $gates: any; $i18n: any; cacheKey?: string }) {
    const cacheKey = context.cacheKey ?? ''
    if (this.cacheKey === cacheKey && this.items.length) return

    this.syncing = true

    const canAccess = (path: string) =>
      context.$grants.access(path) && context.$features.access(path) && context.$gates.access(path)

    const t = (key: string | undefined): string => (key ? context.$i18n.t(key) : '')

    const navigationEntries = [manageGroups, minisiteGroups, accountGroups]
      .flatMap((groups) => settingsNavigationSearchEntries(groups as SettingsNavigationGroup[]))

    // Prefer the navigation registry's page metadata, then add the generated
    // section/field rows. A route may appear in both sources, so page entries
    // are de-duplicated while their more-specific rows remain searchable.
    const sourceEntries = [...navigationEntries, ...(searchManifest as SearchSourceEntry[])]
    const seenEntries = new Set<string>()
    const uniqueEntries = sourceEntries.filter((entry) => {
      const key = [entry.kind, entry.pagePath, entry.sectionId, entry.fieldLabel].join(':')
      if (seenEntries.has(key)) return false
      seenEntries.add(key)
      return true
    })

    this.items = uniqueEntries
      .filter((e) => canAccess(e.pagePath))
      .map((e) => {
        // Pick the most specific label/info this entry carries — field beats
        // section beats page — and resolve through $t.
        const label =
          e.kind === 'field'   ? e.fieldLabel :
          e.kind === 'section' ? e.sectionLabel :
                                 e.pageTitle
        const info =
          e.kind === 'field'   ? e.fieldInfo :
          e.kind === 'page'    ? e.pageInfo :
                                 undefined

        return {
          ...e,
          searchableLabel: t(label),
          searchableInfo: t(info),
          searchablePageTitle: t(e.pageTitle),
          searchableSectionLabel: t(e.sectionLabel),
          searchableTags: (e.searchTags ?? []).join(' '),
          href: e.sectionId && e.kind !== 'page' ? `${e.pagePath}#${e.sectionId}` : e.pagePath,
        } satisfies IndexedEntry
      })

    this.fuse = markRaw(new Fuse(this.items as IndexedEntry[], {
      keys: [
        { name: 'searchableLabel',       weight: 4 },
        { name: 'searchablePageTitle',   weight: 2 },
        { name: 'searchableSectionLabel', weight: 1.5 },
        { name: 'searchableInfo',        weight: 1 },
        { name: 'searchableTags',        weight: 1 },
      ],
      threshold: 0.3,
      ignoreLocation: true,
    }))
    this.cacheKey = cacheKey
    this.syncing = false
  }

  override search (query: string) {
    const filtered = (this.fuse?.search(query) ?? [])
      .slice(0, 10)
      .map(({ item }) => new SettingsResult({ data: item }))

    this.results = filtered
    return true
  }
}

class SettingsResult extends Result {
  /** Used by the parent `Search.vue` to drive `router.push(...)`. */
  override get path (): string {
    return (this as { data: IndexedEntry }).data.href
  }
}

export default new Settings()
